import { BlogSection } from '@/types/blog';

export const fuenf_arten_der_liebe: BlogSection = {
  id: 'fuenf-arten-der-liebe',
  title: 'Fünf Arten der Liebe (nach Fromm)',
  content: `
    <p>Fromm warnt: Liebe ist nicht nur romantische Liebe. Wer „Die Kunst des Liebens" als Beziehungsratgeber liest, verfehlt den Kern.</p>

    <p>Liebe – verstanden als <strong>aktive Sorge für das Leben und Wachstum</strong> – zeigt sich in fünf Formen:</p>

    <h3 class="text-2xl font-bold text-joerg-800 dark:text-joerg-100 mt-8 mb-4">1. Brüderliche Liebe (Brotherly Love)</h3>
    
    <p><strong>Was es ist:</strong> Die grundlegendste Form. Liebe zu allen Menschen – nicht als Verwandte, sondern als Mitmenschen.</p>

    <div class="bg-gradient-to-br from-sage-50 to-joerg-50 dark:from-sage-900 dark:to-joerg-900 p-6 rounded-lg border border-sage-200 dark:border-sage-800 my-6">
      <p class="text-joerg-700 dark:text-joerg-300 italic"><strong>Fromm:</strong> „Brüderliche Liebe ist Liebe zwischen Gleichen."</p>
    </div>

    <p><strong>Nicht:</strong></p>
    <ul class="list-disc pl-6 space-y-2 my-4">
      <li>„Ich liebe nur die, die mir nützen." (Haben-Modus, transaktional)</li>
      <li>„Ich helfe nur, wenn ich etwas zurückbekomme." (Kalkül)</li>
    </ul>

    <p><strong>Sondern:</strong></p>
    <ul class="list-disc pl-6 space-y-2 my-4">
      <li>„Ich sehe den Menschen im Menschen." (Sein-Modus, Humanismus)</li>
      <li>„Ich respektiere die Würde jedes Einzelnen." (Grundhaltung)</li>
    </ul>

    <p class="text-sm text-joerg-600 dark:text-joerg-400 italic mt-4">💼 <strong>Business-Übersetzung:</strong> „Meine Kunden sind Menschen mit Bedürfnissen" – nicht Umsatzquellen.</p>

    <h3 class="text-2xl font-bold text-joerg-800 dark:text-joerg-100 mt-12 mb-4">2. Mütterliche Liebe (Motherly Love)</h3>
    
    <p><strong>Was es ist:</strong> Bedingungslose Liebe. Nicht, weil das Kind etwas leistet, sondern weil es <em>ist</em>.</p>

    <div class="bg-gradient-to-br from-sage-50 to-joerg-50 dark:from-sage-900 dark:to-joerg-900 p-6 rounded-lg border border-sage-200 dark:border-sage-800 my-6">
      <p class="text-joerg-700 dark:text-joerg-300 italic"><strong>Fromm:</strong> „Mütterliche Liebe ist Bejahung des Lebens des Kindes und seiner Bedürfnisse."</p>
    </div>

    <p><strong>ABER – Fromms Warnung:</strong> Mütterliche Liebe darf nicht zum Gefängnis werden. Echte mütterliche Liebe will, dass das Kind wächst und sich löst.</p>

    <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4 rounded-lg my-4">
      <p class="text-amber-800 dark:text-amber-200 text-sm italic">„Die Mutter muss nicht nur das Leben des Kindes tolerieren, sondern es auch aus sich entlassen können."</p>
    </div>

    <p class="text-sm text-joerg-600 dark:text-joerg-400 italic mt-4">💼 <strong>Business-Übersetzung:</strong> Als Führungskraft Menschen so entwickeln, dass sie fliegen können – nicht festhalten, sondern vertrauen. Als Coach Klienten so stärken, dass sie auch ohne dich weiterwachsen. Das ist wahre Führung: loslassen, damit andere ihre Flügel finden.</p>

    <h3 class="text-2xl font-bold text-joerg-800 dark:text-joerg-100 mt-12 mb-4">3. Erotische Liebe (Erotic Love)</h3>
    
    <p><strong>Was es ist:</strong> Die Liebe zwischen zwei Menschen – exklusiv, intensiv, verletzlich.</p>

    <p><strong>Paradox:</strong> Erotische Liebe ist exklusiv (nicht alle, sondern diese eine Person). ABER: Sie basiert auf brüderlicher Liebe (sonst ist es nur Begehren, nicht Liebe).</p>

    <div class="bg-gradient-to-br from-sage-50 to-joerg-50 dark:from-sage-900 dark:to-joerg-900 p-6 rounded-lg border border-sage-200 dark:border-sage-800 my-6">
      <p class="text-joerg-700 dark:text-joerg-300 italic"><strong>Fromm:</strong> „Wenn ich jemanden wirklich liebe, liebe ich alle Menschen, liebe ich die Welt, liebe ich das Leben. Wenn ich zu einem Menschen sagen kann: ‚Ich liebe dich', muss ich sagen können: ‚Ich liebe in dir alle, ich liebe durch dich die Welt, ich liebe in dir auch mich selbst.'"</p>
    </div>

    <p class="text-sm text-joerg-600 dark:text-joerg-400 italic mt-4">💼 <strong>Business-Übersetzung:</strong> Wenn du einen Kunden wirklich liebst (Fürsorge, Respekt, Erkenntnis), öffnet dich das für alle Kunden. „Durch diesen Kunden lerne ich, was Menschen wirklich brauchen."</p>

    <h3 class="text-2xl font-bold text-joerg-800 dark:text-joerg-100 mt-12 mb-4">4. Selbstliebe (Self-Love)</h3>
    
    <p><strong>Was es ist:</strong> Nicht Egoismus. Nicht Narzissmus. Sondern: Die Fähigkeit, sich selbst mit denselben Prinzipien zu begegnen wie anderen.</p>

    <div class="bg-gradient-to-br from-sage-50 to-joerg-50 dark:from-sage-900 dark:to-joerg-900 p-6 rounded-lg border border-sage-200 dark:border-sage-800 my-6">
      <p class="text-joerg-700 dark:text-joerg-300 italic"><strong>Fromm:</strong> „Wenn es eine Tugend ist, meinen Nächsten als menschliches Wesen zu lieben, muss es auch eine Tugend – und nicht ein Laster – sein, mich selbst zu lieben, denn auch ich bin ein menschliches Wesen."</p>
    </div>

    <p><strong>Fromms Kern-These:</strong> „Liebe zu anderen und Liebe zu uns selbst schließen einander nicht aus, sondern bedingen einander."</p>

    <p class="text-sm text-joerg-600 dark:text-joerg-400 italic mt-4">💼 <strong>Business-Übersetzung:</strong> „Ich sorge für mich, damit ich für andere sorgen kann" – nicht „Ich opfere mich für mein Business." Dein Nordstern: <em>Wirtschaftlicher, nachhaltiger Erfolg bei andauernder Gesundheit.</em> = Selbstliebe im Fromm'schen Sinne.</p>

    <h3 class="text-2xl font-bold text-joerg-800 dark:text-joerg-100 mt-12 mb-4">5. Gottesliebe (Love of God)</h3>
    
    <p><strong>Was es ist:</strong> Nicht religiös im dogmatischen Sinne. Sondern: Liebe zum Leben, zum Sein selbst.</p>

    <div class="bg-gradient-to-br from-sage-50 to-joerg-50 dark:from-sage-900 dark:to-joerg-900 p-6 rounded-lg border border-sage-200 dark:border-sage-800 my-6">
      <p class="text-joerg-700 dark:text-joerg-300 italic"><strong>Fromm (humanistisch):</strong> „Gott ist ein Symbol für das, was der Mensch werden kann, für das, was in ihm angelegt ist."</p>
    </div>

    <p class="text-sm text-joerg-600 dark:text-joerg-400 italic mt-4">💼 <strong>Business-Übersetzung:</strong> Nicht „Ich muss Erfolg haben" (Gott = externes Ziel), sondern „Ich bin Teil eines größeren Prozesses" (Gott = Verbundenheit mit Leben/Welt/Sinn).</p>
  `
};
